import { graphql, useStaticQuery } from "gatsby";

export interface ArticleNode {
  id: string;
  slug: string;
  sub_category: {
    id: string;
    name: string;
    main_category: {
      id: string;
      name: string;
      slug: string;
    };
  };
  article_type: {
    name: string | null;
  } | null;
  keywords: {
    keyword: string | null;
  } | null;
  parent_article: {
    id: string;
  } | null;
  Event_info: {
    Start_date: string | null;
  } | null;
}

export interface ArticleHierarchy {
  id: string;
  slug: string;
  Page_title: string;
  readonly Page_basics: {
    readonly Page_media: {
      readonly ThumbnailImage: {
        readonly localFile: any;
      } | null;
    } | null;
    summary: string | null;
    introduction: {
      readonly data: {
        readonly childrenMarkdownRemark: ReadonlyArray<{
          readonly html: string | null;
        } | null> | null;
      } | null;
    };
  } | null;
  sub_category: {
    id: string;
    name: string;
    main_category: {
      id: string;
      name: string;
      slug: string;
    };
  };
  parent_article: {
    id: string;
  } | null;
  children?: ArticleHierarchy[];
  Event_info: {
    Start_date: string | null;
  } | null;
  readonly keywords: ReadonlyArray<{
    readonly keyword: string | null;
  } | null> | null;
  readonly article_type: {
    name: string | null;
  } | null;
}

export const useArticleHierarchy = () => {
  const { allStrapiArticle, strapiHomepage } = useStaticQuery(graphql`
    query ArticleHierarchy {
      allStrapiArticle {
        edges {
          node {
            Page_title
            id
            slug
            keywords {
              keyword
            }
            article_type {
              name
            }
            sub_category {
              name
              id
              slug
              main_category {
                name
                slug
                id
              }
            }
            parent_article {
              id
              slug
            }
            Page_basics {
              Page_media {
                ThumbnailImage {
                  localFile {
                    extension
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        width: 300
                        placeholder: BLURRED
                        formats: [AUTO, WEBP, AVIF]
                      )
                    }
                  }
                }
              }
              summary
              introduction {
                data {
                  childrenMarkdownRemark {
                    html
                  }
                }
              }
            }
            Event_info {
              Start_date(formatString: "D MMMM YYYY", locale: "en")
            }
          }
        }
      }
      strapiHomepage {
        sections {
          main_category {
            slug
            sub_categories {
              id
              name
              slug
              articles_standard_pages {
                id
                slug
              }
            }
            name
            id
          }
        }
      }
    }
  `);

  const buildHierarchy = (
    articles: ArticleNode[],
    parentId?: string,
    ancestors?: string[],
  ): ArticleHierarchy[] => {
    const hierarchy: ArticleHierarchy[] = [];
    if (parentId && ancestors) {
      ancestors.push(parentId);
    }
    articles
      .filter((article) =>
        parentId
          ? article.parent_article?.id === parentId &&
            (ancestors ? !ancestors?.includes(article.id) : true)
          : true,
      )
      .forEach((article) => {
        const subHierarchy = buildHierarchy(
          articles,
          article.id,
          ancestors || [],
        );
        if (subHierarchy.length > 0) {
          (article as ArticleHierarchy).children = subHierarchy;
        }
        hierarchy.push(article as ArticleHierarchy);
      });

    return hierarchy;
  };

  const articles = allStrapiArticle.edges.map((edge: any) => edge.node);

  const articleHierarchy = buildHierarchy(articles)
    .filter((article) => (article.children ? !!article.children.length : false))
    .filter((article) => !article.parent_article)
    .filter((article) => article.sub_category)
    .filter((article) => article.sub_category.main_category);

  const hierarchy = strapiHomepage.sections
    .map((section: any) => section.main_category)
    .reduce((acc: any, mainCategory: any) => {
      if (!mainCategory) return acc;
      acc[mainCategory.id] = {
        type: "mainCategory",
        ...mainCategory,
        subCategories: mainCategory.sub_categories
          ? mainCategory.sub_categories.map((subCategory: any) => {
              return {
                ...subCategory,
                articles: subCategory.articles_standard_pages
                  ? subCategory.articles_standard_pages.map((item: any) =>
                      articleHierarchy.find((art) => art.id === item.id),
                    )
                  : null,
              };
            })
          : null,
      };
      return acc;
    }, {});

  const getAncestorsForArticleId = (id: string) => {
    let article = articles.find((article: any) => article.id === id);
    const ancestors = [{ type: "article", ...article }];
    while (article) {
      const parent =
        article.parent_article &&
        !ancestors
          .map((ancestor) => ancestor.id)
          .includes(article.parent_article.id)
          ? {
              type: "article",
              ...articles.find(
                (art: any) => art.id === article.parent_article.id,
              ),
            }
          : article.sub_category
          ? { type: "subCategory", ...article.sub_category }
          : null;
      if (parent) {
        ancestors.push(parent);
        article = parent;
      } else {
        article = null;
      }
    }
    return ancestors;
  };

  const getDescendantsForArticleId = (id: string) => {
    const article = buildHierarchy(articles)
      .filter((article) =>
        article.children ? !!article.children.length : false,
      )
      .find((art) => art.id === id);
    if (!article) return null;
    return article.children;
  };

  const getMainCategory = (mainCategoryId: string) => {
    return hierarchy[mainCategoryId];
  };

  // console.log({ hierarchy });
  return {
    hierarchy,
    articles,
    articleHierarchy,
    getAncestorsForArticleId,
    getDescendantsForArticleId,
    getMainCategory,
  };
};
